import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import vulnApi from '../../services/vulnApi';
import useVulnFilters from '../../hooks/useVulnFilters';
import VulnFilterBar from '../../components/vuln/VulnFilterBar';

const selectStyle = "text-sm text-slate-600 dark:text-slate-300 bg-transparent border-0 border-b border-slate-300 dark:border-slate-600 focus:border-indigo-500 focus:ring-0 py-1 pr-8 pl-1 cursor-pointer appearance-none";
const selectBg = { backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center' };

function VulnFindings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterScanId, setFilterScanId] = useState(searchParams.get('scan_id') || '');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const { customerId, assetGroupId, setCustomerId, setAssetGroupId } = useVulnFilters();

  const fetchFindings = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (customerId && !filterScanId) params.customer_id = customerId;
      if (filterScanId) params.scan_id = filterScanId;
      if (assetGroupId) params.asset_group_id = assetGroupId;
      if (filterSeverity) params.severity = filterSeverity;
      if (filterStatus) params.status = filterStatus;
      const data = await vulnApi.getFindings(params);
      setFindings(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (err) {
      setError(err.message || 'Failed to load findings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFindings();
  }, [customerId, assetGroupId, filterScanId, filterSeverity, filterStatus]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === findings.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(findings.map(f => f.id)));
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (selected.size === 0) return;
    try {
      await vulnApi.bulkUpdateStatus(Array.from(selected), newStatus);
      setNotification({ type: 'success', text: `${selected.size} finding(s) marked as ${newStatus}` });
      fetchFindings();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const severityBadge = (sev) => {
    const styles = {
      critical: 'bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400',
      high: 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400',
      medium: 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400',
      low: 'bg-sky-100 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400',
      info: 'bg-slate-100 dark:bg-slate-500/20 text-slate-500 dark:text-slate-400',
    };
    return styles[sev] || styles.info;
  };

  const statusBadge = (status) => {
    const styles = {
      open: 'bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400',
      fixed: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
      accepted: 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400',
      false_positive: 'bg-slate-100 dark:bg-slate-500/20 text-slate-500 dark:text-slate-400',
    };
    return styles[status] || styles.open;
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">

            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                  Findings{' '}
                  {!loading && (
                    <span className="text-slate-400 dark:text-slate-500 font-medium text-xl">
                      ({findings.length})
                    </span>
                  )}
                </h1>
              </div>

              <VulnFilterBar
                customerId={customerId}
                assetGroupId={assetGroupId}
                onCustomerChange={(id) => { setCustomerId(id); setFilterScanId(''); setSearchParams({}); }}
                onAssetGroupChange={setAssetGroupId}
              >
                <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className={selectStyle} style={selectBg}>
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="info">Info</option>
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectStyle} style={selectBg}>
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="fixed">Fixed</option>
                  <option value="accepted">Accepted</option>
                  <option value="false_positive">False Positive</option>
                </select>
                {filterScanId && (
                  <button
                    onClick={() => { setFilterScanId(''); setSearchParams({}); }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-500/20"
                  >
                    Scan #{filterScanId}
                    <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                )}
              </VulnFilterBar>
            </div>

            {notification && (
              <div
                className={`px-4 py-3 rounded-sm mb-6 ${
                  notification.type === 'success'
                    ? 'bg-emerald-100 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400'
                    : 'bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{notification.text}</span>
                  <button onClick={() => setNotification(null)} className="ml-4 text-current opacity-70 hover:opacity-100">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                </div>
              </div>
            )}

            {/* Bulk actions */}
            {selected.size > 0 && (
              <div className="flex items-center space-x-3 mb-4 bg-slate-100 dark:bg-slate-700/50 px-4 py-3 rounded-sm">
                <span className="text-sm text-slate-600 dark:text-slate-300">{selected.size} selected</span>
                <button onClick={() => handleBulkStatus('fixed')} className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline">Mark Fixed</button>
                <button onClick={() => handleBulkStatus('accepted')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Accept Risk</button>
                <button onClick={() => handleBulkStatus('false_positive')} className="text-sm text-slate-500 hover:underline">False Positive</button>
                <button onClick={() => handleBulkStatus('open')} className="text-sm text-rose-600 dark:text-rose-400 hover:underline">Reopen</button>
              </div>
            )}

            {error && (
              <div className="bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400 px-4 py-3 rounded-sm mb-6">
                {error}
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
              </div>
            )}

            {!loading && !error && (
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                <div className="overflow-x-auto">
                  <table className="table-auto w-full">
                    <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-3 py-3 whitespace-nowrap w-px">
                          <input
                            type="checkbox"
                            checked={findings.length > 0 && selected.size === findings.length}
                            onChange={toggleAll}
                            className="rounded border-slate-300 dark:border-slate-600"
                          />
                        </th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Title</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">CVE</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">CVSS</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Severity</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Status</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Service</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Confidence</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Detected</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                      {findings.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                            No findings match the current filters.
                          </td>
                        </tr>
                      ) : (
                        findings.map((f) => (
                          <tr key={f.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                            <td className="px-3 py-3 whitespace-nowrap w-px">
                              <input
                                type="checkbox"
                                checked={selected.has(f.id)}
                                onChange={() => toggleSelect(f.id)}
                                className="rounded border-slate-300 dark:border-slate-600"
                              />
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap font-medium text-slate-800 dark:text-slate-100 max-w-xs truncate" title={f.title}>
                              {f.title}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap font-mono text-xs text-slate-600 dark:text-slate-300">
                              {f.cve_id || '-'}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
                              {f.cvss_score != null ? f.cvss_score.toFixed(1) : '-'}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              <span className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 text-xs capitalize ${severityBadge(f.severity)}`}>
                                {f.severity}
                              </span>
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              <span className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 text-xs capitalize ${statusBadge(f.status)}`}>
                                {f.status === 'false_positive' ? 'False Pos.' : f.status}
                              </span>
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300 text-xs">
                              {f.affected_service || '-'}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300 text-xs capitalize">
                              {f.match_confidence || '-'}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
                              {f.first_detected ? new Date(f.first_detected).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

export default VulnFindings;
